import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Save, Send } from "lucide-react";
import {
  CHANNEL_LABEL,
  CORRECTIONS_MANUAL_DAYS,
  DEFAULT_BLOGS_PER_WEEK,
  DEFAULT_NEW_SHARE,
  DEFAULT_VETO_HOURS,
  MAX_BLOGS_PER_WEEK,
  MIN_BLOGS_PER_WEEK,
  MODE_LABEL,
  POLICY_CHANNELS,
  addDays,
  channelConnected,
  connectionName,
  correctionsOfPolicy,
  defaultPolicy,
  describeCorrections,
  describeShare,
  policyDateOf,
  policyDayKey,
  usePublishPolicy,
  useSavePublishPolicy,
  type ChannelPolicy,
  type CorrectionsPolicy,
  type PolicyChannel,
  type PolicyMode,
  type PublishPolicy,
} from "@/components/content-simple/agentic";
import { dayKey } from "@/components/content-simple/helpers";

const MODES: PolicyMode[] = ["manual", "veto", "auto"];

const MODE_HELP: Record<PolicyMode, string> = {
  manual: "You approve every piece before it goes out.",
  veto: "The piece publishes by itself after the window unless you hold it.",
  auto: "Publishes as soon as it passes the gate.",
};

const CHANNEL_DESC: Record<PolicyChannel, string> = {
  blog: "Articles on your WordPress site",
  linkedin: "LinkedIn posts",
  x: "Posts on X",
  facebook: "Facebook page posts",
  email: "Email campaigns from Rogers",
  ad: "Ads from Pete",
};

const MIN_HOURS = 1;
const MAX_HOURS = 24 * 14;

function hoursOf(p: PublishPolicy, ch: PolicyChannel): number {
  return p[ch].vetoHours ?? DEFAULT_VETO_HOURS;
}

/** The blog's cadence settings with the defaults the server would apply, so an untouched card never looks changed. */
function blogOf(p: PublishPolicy) {
  return {
    perWeek: p.blog.blogsPerWeek ?? DEFAULT_BLOGS_PER_WEEK,
    newShare: p.blog.newUpdateRatio ?? DEFAULT_NEW_SHARE,
    requireQa: p.blog.requireIndependentQa !== false,
  };
}

function sameBlog(a: PublishPolicy, b: PublishPolicy): boolean {
  const x = blogOf(a);
  const y = blogOf(b);
  return x.perWeek === y.perWeek && Math.round(x.newShare * 100) === Math.round(y.newShare * 100) && x.requireQa === y.requireQa;
}

function correctionsHours(c: CorrectionsPolicy): number {
  return c.vetoHours ?? DEFAULT_VETO_HOURS;
}

function sameCorrections(a: PublishPolicy, b: PublishPolicy): boolean {
  const x = correctionsOfPolicy(a);
  const y = correctionsOfPolicy(b);
  return x.mode === y.mode && (x.mode !== "veto" || correctionsHours(x) === correctionsHours(y)) && policyDayKey(x.manualUntil) === policyDayKey(y.manualUntil);
}

function samePolicy(a: PublishPolicy, b: PublishPolicy): boolean {
  return (
    POLICY_CHANNELS.every((ch) => a[ch].mode === b[ch].mode && (a[ch].mode !== "veto" || hoursOf(a, ch) === hoursOf(b, ch))) &&
    sameBlog(a, b) &&
    !!a.facebook.alsoInstagram === !!b.facebook.alsoInstagram &&
    sameCorrections(a, b)
  );
}

function goToConnections() {
  const el = document.getElementById("channel-connections");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  else window.location.assign("/settings?tab=publishing");
}

const badHoursOf = (hours: number) => !Number.isInteger(hours) || hours < MIN_HOURS || hours > MAX_HOURS;

/**
 * Workspace setting: how each channel publishes once a piece passes the pre-publish check.
 * Manual = you approve every piece. Veto window = it publishes by itself after N hours unless you hold it.
 * Automatic = it publishes as soon as it passes the check. Saved with PUT /api/workspaces/:id/publish-policy.
 *
 * The blog channel also carries its cadence (posts a week, the share that are updates) and whether independent QA is required,
 * and a separate "Corrections" entry says how fixes to already-live posts are released.
 */
export function PublishPolicySetting({
  workspace,
  disabled,
  disabledReason,
}: {
  workspace: any;
  disabled?: boolean;
  /** Shown as a note when `disabled` (the caller lacks the role to change the policy). */
  disabledReason?: string;
}) {
  const { policy: saved, available, isLoading, isError } = usePublishPolicy();
  const save = useSavePublishPolicy();
  const [draft, setDraft] = useState<PublishPolicy>(() => saved ?? defaultPolicy());

  // Follow the server whenever it changes (first load, a save, another tab) so the card never shows stale values.
  useEffect(() => {
    setDraft(saved ?? defaultPolicy());
  }, [saved]);

  const base = saved ?? defaultPolicy();
  const dirty = useMemo(() => !samePolicy(draft, base), [draft, base]);
  const correctionsChanged = useMemo(() => !sameCorrections(draft, base), [draft, base]);
  const corrections = correctionsOfPolicy(draft);
  const badHours =
    POLICY_CHANNELS.some((ch) => draft[ch].mode === "veto" && badHoursOf(hoursOf(draft, ch))) ||
    (corrections.mode === "veto" && badHoursOf(correctionsHours(corrections)));
  const locked = !!disabled || !available || save.isPending;

  // A mode switch keeps everything else the channel carries (the blog's cadence); only the veto window is tied to the mode.
  const setMode = (ch: PolicyChannel, mode: PolicyMode) =>
    setDraft((d) => {
      const { vetoHours: _drop, ...rest } = d[ch];
      return { ...d, [ch]: { ...rest, mode, ...(mode === "veto" ? { vetoHours: hoursOf(d, ch) } : {}) } };
    });
  const setHours = (ch: PolicyChannel, hours: number) => setDraft((d) => ({ ...d, [ch]: { ...d[ch], mode: "veto", vetoHours: hours } }));
  const setBlog = (patch: Partial<ChannelPolicy>) => setDraft((d) => ({ ...d, blog: { ...d.blog, ...patch } }));
  const setCorrections = (patch: Partial<CorrectionsPolicy>) => setDraft((d) => ({ ...d, corrections: { ...correctionsOfPolicy(d), ...patch } }));

  const onSave = () => {
    // The corrections entry is only sent when the owner changed it: a save for another reason must not rewrite the server's own burn-in date.
    if (correctionsChanged) save.mutate(draft);
    else {
      const { corrections: _c, ...rest } = draft;
      save.mutate(rest as PublishPolicy);
    }
  };

  const blog = blogOf(draft);
  const newPosts = Math.round(blog.perWeek * blog.newShare);
  const today = dayKey(new Date());
  const holdUntil = policyDayKey(corrections.manualUntil);
  const holdPassed = !!holdUntil && holdUntil < today;
  const correctionsHoursValue = correctionsHours(corrections);
  const invalidCorrHours = corrections.mode === "veto" && badHoursOf(correctionsHoursValue);
  const savedCorrections = correctionsOfPolicy(base);

  return (
    <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-publish-policy">
      <div className="flex items-center gap-3 mb-2">
        <Send size={20} className="text-blue-400" />
        <h2 className="font-display font-bold text-foreground">Publishing policy</h2>
      </div>
      <p className="text-xs text-muted-foreground/70" data-testid="text-publish-policy-description">
        Decide, channel by channel, what happens once a piece from the Claude team passes its pre-publish check. Manual: you approve every piece. Veto: the piece publishes by itself after the window unless you hold it. Automatic: publishes as soon as it passes the gate.
      </p>

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Loading your policy…</div>
      )}
      {!isLoading && isError && (
        <p className="text-xs text-red-300" data-testid="text-publish-policy-error">Couldn't load your publishing policy. Reload the page to try again.</p>
      )}
      {!isLoading && !isError && !available && (
        <p className="text-xs text-muted-foreground" data-testid="text-publish-policy-unavailable">
          Publishing policies aren't available on this server yet, so every channel waits for your approval.
        </p>
      )}

      <div className="space-y-3">
        {/* email has its own card (Settings -> Email -> Rogers' sending policy): its modes mean "may Rogers send lead email by himself", not "publish a piece" */}
        {POLICY_CHANNELS.filter((ch) => ch !== "email").map((ch) => {
          const mode = draft[ch].mode;
          const connected = channelConnected(workspace, ch);
          const hours = hoursOf(draft, ch);
          const invalidHours = mode === "veto" && badHoursOf(hours);
          return (
            <fieldset key={ch} className="rounded-xl border border-border bg-muted/20 p-3 md:p-4 space-y-3" data-testid={`policy-channel-${ch}`} disabled={locked}>
              <legend className="sr-only">{CHANNEL_LABEL[ch]} publishing</legend>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{CHANNEL_LABEL[ch]}</p>
                  <p className="text-[11px] text-muted-foreground/70">{CHANNEL_DESC[ch]}</p>
                </div>
                {connected !== null && (
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] font-medium ${connected ? "text-emerald-400" : "text-amber-400"}`}
                    data-testid={`policy-connection-${ch}`}
                    data-connected={connected}
                  >
                    {connected ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
                    {connectionName(ch)} {connected ? "connected" : "not connected"}
                    {!connected && (
                      <button type="button" onClick={goToConnections} className="ml-1 underline underline-offset-2" data-testid={`link-connect-${ch}`}>
                        Connect it
                      </button>
                    )}
                  </span>
                )}
              </div>

              <div role="radiogroup" aria-label={`${CHANNEL_LABEL[ch]} publishing mode`} className="grid grid-cols-3 gap-2">
                {MODES.map((m) => {
                  const checked = mode === m;
                  return (
                    <label
                      key={m}
                      className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-center transition-all sm:flex-row sm:items-start sm:gap-2 sm:px-3 sm:text-left ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${
                        checked ? "border-blue-500/40 bg-blue-500/10" : "border-border bg-background/30 hover:border-border/70"
                      }`}
                      data-testid={`option-policy-${ch}-${m}`}
                    >
                      <input
                        type="radio"
                        name={`policy-${ch}`}
                        value={m}
                        checked={checked}
                        disabled={locked}
                        onChange={() => setMode(ch, m)}
                        className="accent-blue-500 sm:mt-1"
                        data-testid={`radio-policy-${ch}-${m}`}
                      />
                      <span className="min-w-0 text-xs font-medium leading-tight text-foreground sm:text-sm">{MODE_LABEL[m]}</span>
                    </label>
                  );
                })}
              </div>

              {mode === "veto" && (
                <div className="flex items-center gap-2 flex-wrap">
                  <label htmlFor={`hours-${ch}`} className="text-xs text-muted-foreground">Window (hours)</label>
                  <input
                    id={`hours-${ch}`}
                    type="number"
                    inputMode="numeric"
                    min={MIN_HOURS}
                    max={MAX_HOURS}
                    step={1}
                    value={Number.isFinite(hours) ? hours : ""}
                    disabled={locked}
                    onChange={(e) => setHours(ch, e.target.value === "" ? NaN : Number(e.target.value))}
                    className={`w-24 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground ${invalidHours ? "border-red-500/60" : "border-border"}`}
                    aria-invalid={invalidHours}
                    data-testid={`input-veto-hours-${ch}`}
                  />
                  <span className="text-xs text-muted-foreground">
                    {invalidHours ? `Enter a whole number from ${MIN_HOURS} to ${MAX_HOURS}.` : `You have ${hours} hour${hours === 1 ? "" : "s"} to hold it before it goes out.`}
                  </span>
                </div>
              )}

              <p className="text-[11px] text-muted-foreground/80" data-testid={`policy-help-${ch}`}>{MODE_HELP[mode]}</p>

              {mode !== "manual" && connected === false && (
                <p className="flex items-start gap-1.5 text-[11px] text-amber-400" data-testid={`policy-warning-${ch}`}>
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  <span>
                    {mode === "auto" ? "Automatic" : "The veto window"} has no effect until the channel is connected. Until then every piece for it waits for your approval.
                  </span>
                </p>
              )}

              {ch === "facebook" && (
                <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background/30 p-3" data-testid="facebook-also-instagram">
                  <div className="min-w-0">
                    <label htmlFor="switch-also-instagram" className="text-xs font-semibold text-foreground">Also post to Instagram</label>
                    <p className="text-[11px] text-muted-foreground/80">Each Facebook post that has an image goes to the Page's linked Instagram business account too, with the same caption, under the same approval and checks. Posts without an image stay on Facebook only.</p>
                  </div>
                  <button
                    id="switch-also-instagram"
                    type="button"
                    role="switch"
                    aria-checked={!!draft.facebook.alsoInstagram}
                    disabled={locked}
                    onClick={() => setDraft((d) => ({ ...d, facebook: { ...d.facebook, alsoInstagram: !d.facebook.alsoInstagram } }))}
                    className={`mt-0.5 flex h-6 w-11 shrink-0 items-center rounded-full border px-0.5 transition-all disabled:cursor-not-allowed disabled:opacity-60 ${draft.facebook.alsoInstagram ? "border-blue-500 bg-blue-600" : "border-border bg-muted/50"}`}
                    data-testid="switch-also-instagram"
                  >
                    <span className={`h-[18px] w-[18px] rounded-full bg-white transition-transform ${draft.facebook.alsoInstagram ? "translate-x-5" : "translate-x-0"}`} />
                  </button>
                </div>
              )}

              {ch === "blog" && (
                <div className="space-y-4 rounded-lg border border-border bg-background/30 p-3" data-testid="blog-cadence">
                  {/* Posts a week */}
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-foreground" id="label-blogs-per-week">Blog posts a week</p>
                    <div role="radiogroup" aria-labelledby="label-blogs-per-week" className="grid grid-cols-6 gap-1.5" data-testid="radiogroup-blogs-per-week">
                      {Array.from({ length: MAX_BLOGS_PER_WEEK - MIN_BLOGS_PER_WEEK + 1 }, (_, i) => MIN_BLOGS_PER_WEEK + i).map((n) => {
                        const on = blog.perWeek === n;
                        return (
                          <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            disabled={locked}
                            onClick={() => setBlog({ blogsPerWeek: n })}
                            className={`rounded-lg border py-2 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                              on ? "border-blue-500/50 bg-blue-500/15 text-blue-200" : "border-border bg-background/30 text-foreground hover:border-border/70"
                            }`}
                            data-testid={`option-blogs-per-week-${n}`}
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-muted-foreground/80" data-testid="text-blogs-per-week-help">
                      Draper's plan holds up to {blog.perWeek} blog post{blog.perWeek === 1 ? "" : "s"} a week, Monday to Saturday.
                    </p>
                  </div>

                  {/* New posts vs updates */}
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <label htmlFor="ratio-new-update" className="text-xs font-semibold text-foreground">New posts and updates</label>
                      <span className="text-xs font-medium tabular-nums text-foreground" data-testid="text-ratio-readout">{describeShare(blog.newShare)}</span>
                    </div>
                    <input
                      id="ratio-new-update"
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={Math.round(blog.newShare * 100)}
                      disabled={locked}
                      onChange={(e) => setBlog({ newUpdateRatio: Number(e.target.value) / 100 })}
                      aria-valuetext={describeShare(blog.newShare)}
                      className="h-6 w-full cursor-pointer accent-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                      data-testid="input-ratio-new-update"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground/70" aria-hidden="true">
                      <span>All updates</span>
                      <span>All new</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground/80" data-testid="text-ratio-help">
                      At {blog.perWeek} a week that is about {newPosts} new and {blog.perWeek - newPosts} {blog.perWeek - newPosts === 1 ? "update" : "updates"}. An update refreshes a post that is already live; it keeps its address.
                    </p>
                  </div>

                  {/* Independent QA */}
                  <div className="space-y-1.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <label htmlFor="switch-require-qa" className="text-xs font-semibold text-foreground">Require independent QA before publishing</label>
                        <p className="text-[11px] text-muted-foreground/80">
                          A second reviewer, apart from the writer, reads the claims against their sources before a blog post can go out. It usually takes under an hour.
                        </p>
                      </div>
                      <button
                        id="switch-require-qa"
                        type="button"
                        role="switch"
                        aria-checked={blog.requireQa}
                        disabled={locked}
                        onClick={() => setBlog({ requireIndependentQa: !blog.requireQa })}
                        className={`mt-0.5 flex h-6 w-11 shrink-0 items-center rounded-full border px-0.5 transition-all disabled:cursor-not-allowed disabled:opacity-60 ${blog.requireQa ? "border-blue-500 bg-blue-600" : "border-border bg-muted/50"}`}
                        data-testid="switch-require-qa"
                      >
                        <span className={`h-[18px] w-[18px] rounded-full bg-white transition-transform ${blog.requireQa ? "translate-x-5" : "translate-x-0"}`} />
                      </button>
                    </div>
                    {!blog.requireQa && (
                      <p className="flex items-start gap-1.5 text-[11px] text-amber-400" data-testid="warning-qa-off">
                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                        <span>With this off, only the automatic checks stand between a draft and your site. They check form (markup, links, wording); independent QA is what checks that the facts are true.</span>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </fieldset>
          );
        })}

        {/* Corrections: fixes to posts that are already live */}
        <fieldset className="rounded-xl border border-border bg-muted/20 p-3 md:p-4 space-y-3" data-testid="policy-corrections" disabled={locked}>
          <legend className="sr-only">Corrections</legend>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">Corrections</p>
              <p className="text-[11px] text-muted-foreground/70">Fixes to posts that are already live, filed by the daily supervisor</p>
            </div>
            <span className="inline-flex items-center rounded-full border border-border bg-background/40 px-2 py-0.5 text-[11px] font-medium text-foreground" data-testid="text-corrections-state">
              {describeCorrections(savedCorrections)}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground/80" data-testid="text-corrections-rationale">
            Corrections rewrite posts that are already live, so the handoff advises approving each one yourself for the first {CORRECTIONS_MANUAL_DAYS} days.
          </p>

          <div role="radiogroup" aria-label="Corrections mode" className="grid grid-cols-3 gap-2">
            {MODES.map((m) => {
              const checked = corrections.mode === m;
              return (
                <label
                  key={m}
                  className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-center transition-all sm:flex-row sm:items-start sm:gap-2 sm:px-3 sm:text-left ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${
                    checked ? "border-blue-500/40 bg-blue-500/10" : "border-border bg-background/30 hover:border-border/70"
                  }`}
                  data-testid={`option-corrections-${m}`}
                >
                  <input
                    type="radio"
                    name="policy-corrections"
                    value={m}
                    checked={checked}
                    disabled={locked}
                    onChange={() => setCorrections({ mode: m, ...(m === "veto" ? { vetoHours: correctionsHoursValue } : {}) })}
                    className="accent-blue-500 sm:mt-1"
                    data-testid={`radio-corrections-${m}`}
                  />
                  <span className="min-w-0 text-xs font-medium leading-tight text-foreground sm:text-sm">{MODE_LABEL[m]}</span>
                </label>
              );
            })}
          </div>

          {corrections.mode === "veto" && (
            <div className="flex items-center gap-2 flex-wrap">
              <label htmlFor="hours-corrections" className="text-xs text-muted-foreground">Window (hours)</label>
              <input
                id="hours-corrections"
                type="number"
                inputMode="numeric"
                min={MIN_HOURS}
                max={MAX_HOURS}
                step={1}
                value={Number.isFinite(correctionsHoursValue) ? correctionsHoursValue : ""}
                disabled={locked}
                onChange={(e) => setCorrections({ mode: "veto", vetoHours: e.target.value === "" ? NaN : Number(e.target.value) })}
                className={`w-24 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground ${invalidCorrHours ? "border-red-500/60" : "border-border"}`}
                aria-invalid={invalidCorrHours}
                data-testid="input-corrections-veto-hours"
              />
              <span className="text-xs text-muted-foreground">
                {invalidCorrHours ? `Enter a whole number from ${MIN_HOURS} to ${MAX_HOURS}.` : `You have ${correctionsHoursValue} hour${correctionsHoursValue === 1 ? "" : "s"} to hold a correction before it goes out.`}
              </span>
            </div>
          )}

          {corrections.mode === "manual" ? (
            <p className="text-[11px] text-muted-foreground/80" data-testid="corrections-help-manual">You approve every correction before it goes out.</p>
          ) : (
            <div className="space-y-1.5" data-testid="corrections-manual-until">
              <div className="flex items-center gap-2 flex-wrap">
                <label htmlFor="corrections-until" className="text-xs text-muted-foreground">Keep corrections manual until</label>
                <input
                  id="corrections-until"
                  type="date"
                  min={today}
                  value={holdUntil}
                  disabled={locked}
                  onChange={(e) => setCorrections({ manualUntil: e.target.value || null })}
                  className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                  data-testid="input-corrections-until"
                />
                <button
                  type="button"
                  disabled={locked}
                  onClick={() => setCorrections({ manualUntil: dayKey(addDays(new Date(), CORRECTIONS_MANUAL_DAYS)) })}
                  className="rounded-md border border-border px-2 py-1.5 text-xs text-foreground hover:bg-muted/40 disabled:opacity-60"
                  data-testid="button-corrections-14-days"
                >
                  {CORRECTIONS_MANUAL_DAYS} days from today
                </button>
                {holdUntil && (
                  <button type="button" disabled={locked} onClick={() => setCorrections({ manualUntil: null })} className="text-xs text-muted-foreground underline underline-offset-2" data-testid="button-corrections-clear-date">
                    Clear
                  </button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground/80" data-testid="corrections-help-until">
                {holdUntil && !holdPassed
                  ? `Until ${policyDateOf(holdUntil)?.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} every correction waits for you, whatever you chose above. After that, ${corrections.mode === "veto" ? `it goes out ${correctionsHoursValue} hours after it passes unless you hold it` : "it goes out as soon as it passes"}.`
                  : holdUntil
                    ? "That date has passed, so the choice above applies now."
                    : "No date is set, so the choice above applies straight away."}
              </p>
            </div>
          )}
        </fieldset>
      </div>

      <button
        data-testid="button-save-publish-policy"
        onClick={onSave}
        disabled={!dirty || locked || badHours}
        title={disabled ? (disabledReason ?? "You don't have permission to change this.") : undefined}
        className="flex items-center gap-2 px-4 py-3 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
      >
        {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Save publishing policy
      </button>
      {disabled && disabledReason && (
        <p className="text-[11px] text-muted-foreground/60" data-testid="text-publish-policy-disabled-reason">{disabledReason}</p>
      )}
    </div>
  );
}
